"use client";

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import MoneyAmount from "@/shared/ui/components/money-amount";
import { CalendarTimeIconPurple } from "@/shared/ui/icons/calender-time-icon-purple";
import type { PaymentOption } from "@/types";

interface RepaymentDueCardProps {
  paymentOption: PaymentOption;
  currency: string;
}

function formatRepaymentDate(dateString: string, locale: string): string {
  const date = new Date(dateString);
  const day = date.getDate();
  const monthName = date.toLocaleDateString(locale, {
    month: "long",
    // ar-SA defaults to Islamic calendar for some runtimes; force Gregorian to match en-US month names
    calendar: "gregory",
  });
  const year = date.getFullYear();

  return `${day} ${monthName} ${year}`;
}

export function RepaymentDueCard({
  paymentOption,
  currency,
}: RepaymentDueCardProps) {
  const { t, i18n } = useTranslation();

  const amount = paymentOption.totalToBeRepaidAmount?.amount ?? 0;
  const locale = i18n.language?.startsWith("ar") ? "ar-SA" : "en-US";

  const formattedDate = useMemo(() => {
    if (!paymentOption.firstRepaymentDate) return null;
    return formatRepaymentDate(paymentOption.firstRepaymentDate, locale);
  }, [paymentOption.firstRepaymentDate, locale]);

  return (
    <div
      className="border border-[#DAE0EB] rounded-[20px] p-4 bg-white"
      data-testid="repayment-due-card"
    >
      <div className="flex items-center gap-4">
        <div className="shrink-0">
          <CalendarTimeIconPurple />
        </div>
        <div className="flex flex-col gap-1.5">
          <div className="text-xs font-medium text-[#585B7A]">
            {t("payment.pnm_no_payment_today")}
          </div>
          <div className="text-sm font-semibold text-[#16181D]">
            <MoneyAmount
              amount={amount}
              currency={currency}
              language={i18n.language}
              className="inline-flex"
            />
            {formattedDate && (
              <span>
                {"  •  "}
                {formattedDate}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
